import * as IF from "infrontjs";
import template from "./detail.html";
import { _lcs, locale } from "../../i18n";

// German source strings, which double as the dictionary keys — these are module
// constants, evaluated before I18n exists, so they are translated where read.
const STATUS_LABELS = {
    draft     : "Entwurf",
    published : "Veröffentlicht"
};

const TYPE_LABELS = {
    hotel      : "Hotel",
    airbnb     : "Airbnb",
    apartment  : "Apartment",
    guesthouse : "Gästehaus",
    bnb        : "B&B",
    hostel     : "Hostel",
    camping    : "Camping",
    other      : "Sonstige"
};

const REGION_LABELS = {
    saechsische_schweiz : "Sächsische Schweiz"
};

/**
 * <accommodation-detail> — read-only modal showing the details of a single
 * accommodation.
 *
 * Usage:
 *   el.open( accommodation, ownerLabel )
 */
class AccommodationDetail extends HTMLElement
{
    constructor()
    {
        super();
        this._connected = false;
    }

    connectedCallback()
    {
        if ( false === this._connected )
        {
            this.app = IF.App.get();
            this.render();
            this.bind();
            this._connected = true;
        }
    }

    render()
    {
        this.app.view.render( this, template, {} );
        this._modal = this.querySelector( '[data-id="ad-modal"]' );
    }

    bind()
    {
        this.querySelector( '[data-id="ad-close"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="ad-close-btn"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="ad-backdrop"]' ).addEventListener( "click", () => this.close() );

        this._onKeydown = ( e ) =>
        {
            if ( e.key === "Escape" && this._modal && !this._modal.hidden )
            {
                this.close();
            }
        };
        document.addEventListener( "keydown", this._onKeydown );
    }

    disconnectedCallback()
    {
        if ( this._onKeydown )
        {
            document.removeEventListener( "keydown", this._onKeydown );
        }
    }

    /* ------------------------------- Public API ------------------------------ */

    open( accommodation, ownerLabel = null )
    {
        if ( !accommodation ) return;

        this._set( "ad-name", accommodation.name || "—" );
        this._set( "ad-slug", accommodation.slug || "—" );
        this._set( "ad-type", TYPE_LABELS[ accommodation.type ]
            ? _lcs( TYPE_LABELS[ accommodation.type ] ) : ( accommodation.type || "—" ) );
        this._html( "ad-status", this._statusBadgeHtml( accommodation.status ) );
        this._set( "ad-region", REGION_LABELS[ accommodation.region ]
            ? _lcs( REGION_LABELS[ accommodation.region ] ) : ( accommodation.region || "—" ) );
        this._set( "ad-owner", ownerLabel || accommodation.ownerUserId || "—" );
        this._set( "ad-timezone", accommodation.timezone || "—" );
        this._html( "ad-www", this._wwwHtml( accommodation.data && accommodation.data.www ) );
        this._set( "ad-address", this._addressText( accommodation.address ) );
        this._set( "ad-created", this._formatDate( accommodation.createdAt ) );
        this._set( "ad-updated", this._formatDate( accommodation.updatedAt ) );
        this._set( "ad-id", accommodation.id || "—" );

        this._modal.hidden = false;
        this.querySelector( '[data-id="ad-close-btn"]' ).focus();
    }

    close()
    {
        this._modal.hidden = true;
    }

    /* ------------------------------- Helpers -------------------------------- */

    _set( id, value )
    {
        const el = this.querySelector( '[data-id="' + id + '"]' );
        if ( el ) el.textContent = value;
    }

    _html( id, value )
    {
        const el = this.querySelector( '[data-id="' + id + '"]' );
        if ( el ) el.innerHTML = value;
    }

    _statusBadgeHtml( status )
    {
        const key = String( status || "" ).toLowerCase();
        const label = STATUS_LABELS[ key ] ? _lcs( STATUS_LABELS[ key ] ) : ( status || "—" );
        return '<span class="acc-status acc-status--' + this._escape( key ) + '">' + this._escape( label ) + '</span>';
    }

    _wwwHtml( www )
    {
        if ( !www ) return "—";
        const safe = this._escape( www );
        const href = /^https?:\/\//i.test( www ) ? safe : "https://" + safe;
        return '<a href="' + href + '" target="_blank" rel="noopener noreferrer">' + safe + "</a>";
    }

    _addressText( address )
    {
        if ( !address || typeof address !== "object" ) return "—";
        const line1 = address.street || "";
        const line2 = [ address.postalCode, address.city ].filter( Boolean ).join( " " );
        const parts = [ line1, line2, address.countryCode ].filter( Boolean );
        return parts.length ? parts.join( ", " ) : "—";
    }

    _escape( str )
    {
        return String( str == null ? "" : str ).replace( /[&<>"']/g, ( c ) =>
            ( { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ c ] ) );
    }

    _formatDate( value )
    {
        if ( !value ) return "—";
        const d = new Date( value );
        if ( isNaN( d.getTime() ) ) return String( value );
        return new Intl.DateTimeFormat( locale(), {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit"
        } ).format( d );
    }
}

customElements.define( "accommodation-detail", AccommodationDetail );
